import { type RouterProps, useHistory } from 'react-router-dom'

import { assert } from '../../../utils'

import { ConnectwareError, ConnectwareErrorType } from '../../../domain'

import { createExtensibleHook } from '../utils'
import { getBreadcrumbConfig } from './Config'
import { getRouteConfig, type RouteParentPath } from './Config'
import {
    AbsoluteRouteOnlyPath,
    type AbsoluteRoutePath,
    type AbsoluteRouteUpdateArgs,
    isAbsoluteRouteOnlyPath,
    isAbsoluteRoutePathWithServiceId,
    type RouterState,
    type RouteUpdateArgs,
} from './Domain'
import { useConnectwareRouting } from './Router.State'
import { generateIntenalPath, mergeState } from './utils'

class RoutingHook {
    constructor (
        protected readonly history: Pick<RouterProps['history'], 'push' | 'createHref' | 'location' | 'goBack'>,
        protected readonly state: RouterState
    ) {}

    protected generateRedirectionPath (...args: RouteUpdateArgs): string {
        return generateIntenalPath(mergeState(this.state, ...args))
    }

    protected getParentAbsoluteRouteUpdateArgs (parent: RouteParentPath): AbsoluteRouteUpdateArgs {
        if (isAbsoluteRouteOnlyPath(parent)) {
            return [parent]
        }

        if (isAbsoluteRoutePathWithServiceId(parent) && this.state.serviceId) {
            return [parent, this.state.serviceId]
        }

        throw new ConnectwareError(ConnectwareErrorType.UNEXPECTED, 'Could not resolve parent path route', { ...this.state, parent })
    }

    redirect (...args: RouteUpdateArgs): void {
        this.history.push(this.generateRedirectionPath(...args))
    }

    redirectToParent (): void {
        const { parent } = getRouteConfig(this.state.path)
        assert(parent, new ConnectwareError(ConnectwareErrorType.UNEXPECTED, 'Route does not have a parent', { ...this.state }))
        this.redirect(...this.getParentAbsoluteRouteUpdateArgs(parent))
    }

    redirectHome (): void {
        this.redirect(AbsoluteRouteOnlyPath.SYSTEM)
    }

    getNavigation (): [AbsoluteRoutePath, ...RouteParentPath[]] {
        const { parents } = getBreadcrumbConfig(this.state.path)
        return [this.state.path, ...parents.map((p) => p.id)]
    }

    /**
     * @returns boolean indicating if the given current route is either the parent route or its child
     */
    isAtRoute (path: AbsoluteRoutePath): boolean {
        return (
            !this.state.path ||
            /**
             * Either it is at the route it-self
             */
            this.state.path === path ||
            /**
             * Or the parent contains it
             */
            getBreadcrumbConfig(this.state.path).parents.some((parent) => parent.id === path)
        )
    }

    goBack (): void {
        this.history.goBack()
    }
}

export const useRouting = createExtensibleHook(() => [useHistory(), useConnectwareRouting()] as const, RoutingHook, 'useRouting')
